/**
 * PhrasalVerbQuiz — fill-in-the-particle.
 *
 * Pattern: front of card is "give up" (verb + particle). Quiz blanks the
 * particle in an example sentence and gives 4 particle options. User picks
 * which particle goes with the verb to mean what's described in `back`.
 *
 * Distractors: particles drawn from OTHER phrasal verbs in the deck.
 * Correct answer's particle is shuffled in.
 *
 * Concrete component (not generalized into core's Cloze) per plan §Issue 5
 * — YAGNI on Cloze parameterization until a 2nd use case lands.
 */

import { useState, useCallback, useMemo } from 'preact/hooks';
import type { Card } from '@kevvykevwin/lang-core/types';
import type {
  QuizMode,
  QuizProps,
  QuizOptionState
} from '@kevvykevwin/lang-core/types';
import {
  QuizLayout,
  QuizQuestion,
  QuizOptions,
  QuizFeedback,
  QuizOption,
  useKeyboard,
  QUIZ_CONFIG,
  type SrsSignals
} from '@kevvykevwin/lang-core';

import type { EnglishCardExt } from '../types.js';

// ============================================
// Helpers
// ============================================

interface PhrasalParts {
  verb: string;
  particles: string[];
}

function splitPhrasalVerb(front: string): PhrasalParts {
  const parts = front.toLowerCase().trim().split(/\s+/);
  if (parts.length < 2) return { verb: parts[0] ?? '', particles: [] };
  return { verb: parts[0], particles: parts.slice(1) };
}

/** Blank out the particle in the example sentence. */
function maskParticleInSentence(sentence: string, particles: string[]): string {
  if (particles.length === 0) return sentence;
  // Match the particle as a whole word, case-insensitive. We blank the FIRST
  // occurrence to avoid over-aggressive masking when the particle word
  // appears elsewhere (e.g. "I want to look up the word" vs "I look up at
  // the sky").
  const target = particles.join(' ');
  const re = new RegExp(`\\b${target.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}\\b`, 'i');
  return sentence.replace(re, '____');
}

function generateOptions(
  correct: string,
  allPhrasalVerbs: Card<EnglishCardExt>[]
): string[] {
  const correctParticles = splitPhrasalVerb(correct).particles.join(' ');
  if (!correctParticles) return [correct];

  const distractorPool = new Set<string>();
  for (const card of allPhrasalVerbs) {
    const { particles } = splitPhrasalVerb(card.front);
    const joined = particles.join(' ');
    if (joined && joined !== correctParticles) distractorPool.add(joined);
  }

  const distractors = [...distractorPool].sort(() => Math.random() - 0.5);
  const options = [correctParticles, ...distractors.slice(0, QUIZ_CONFIG.optionsCount - 1)];

  // Pad if pool was small
  const fallback = ['up', 'down', 'on', 'off', 'in', 'out', 'over', 'after'];
  while (options.length < QUIZ_CONFIG.optionsCount) {
    const next = fallback.find(f => !options.includes(f));
    if (!next) break;
    options.push(next);
  }

  return options.sort(() => Math.random() - 0.5);
}

// ============================================
// Inner component
// ============================================

interface InnerProps {
  card: Card<EnglishCardExt>;
  allCards: Card<EnglishCardExt>[];
  signals: SrsSignals<EnglishCardExt>;
}

function PhrasalVerbQuizInner({ card, allCards, signals }: InnerProps) {
  const answered = signals.quizAnswered.value;
  const { verb, particles } = useMemo(() => splitPhrasalVerb(card.front), [card.front]);
  const correctParticle = particles.join(' ');

  const [options] = useState<string[]>(() => generateOptions(card.front, allCards));
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<{
    message: string;
    type: 'correct' | 'incorrect';
  } | null>(null);

  const maskedSentence = useMemo(
    () => maskParticleInSentence(card.example.sentence, particles),
    [card.example.sentence, particles]
  );

  const handleAnswer = useCallback(
    (index: number) => {
      if (answered || index >= options.length) return;

      const selected = options[index];
      const isCorrect = selected.toLowerCase() === correctParticle.toLowerCase();

      setSelectedIndex(index);
      signals.quizAnswered.value = true;
      signals.updateWordSRS(card.id, isCorrect);

      if (isCorrect) {
        signals.quizCorrect.value++;
        setFeedback({ message: '✅ Correct!', type: 'correct' });
        signals.showNotification('Correct!', 'success', 1500);
      } else {
        signals.quizIncorrect.value++;
        setFeedback({
          message: `❌ Wrong. Answer: ${verb} ${correctParticle} — ${card.back}`,
          type: 'incorrect'
        });
        signals.showNotification(
          `Wrong. ${verb} ${correctParticle} = ${card.back}`,
          'error',
          2500
        );
      }

      setTimeout(() => signals.nextCard(), QUIZ_CONFIG.feedbackDelayMs);
    },
    [answered, card, options, signals, correctParticle, verb]
  );

  useKeyboard({
    currentMode: signals.currentMode,
    quizAnswered: signals.quizAnswered,
    flipCard: signals.flipCard,
    nextCard: signals.nextCard,
    prevCard: signals.prevCard,
    onQuizAnswer: handleAnswer
  });

  const getOptionState = (index: number): QuizOptionState => {
    if (!answered) return 'idle';
    const option = options[index];
    if (option.toLowerCase() === correctParticle.toLowerCase()) return 'correct';
    if (index === selectedIndex) return 'incorrect';
    return 'disabled';
  };

  return (
    <QuizLayout modeName="Phrasal Verb">
      <QuizQuestion label="Pick the missing particle">
        <div className="text-2xl font-medium text-slate-700 mb-3 leading-relaxed">
          {maskedSentence}
        </div>
        <div className="text-sm text-slate-500 italic">
          <span className="font-semibold">{verb}</span> ____ — {card.back}
        </div>
      </QuizQuestion>

      <QuizOptions>
        {options.map((option, index) => (
          <QuizOption
            key={`${option}-${index}`}
            text={option}
            state={getOptionState(index)}
            onClick={() => handleAnswer(index)}
            disabled={answered}
            index={index}
          />
        ))}
      </QuizOptions>

      {feedback && <QuizFeedback message={feedback.message} type={feedback.type} />}
    </QuizLayout>
  );
}

// ============================================
// Factory
// ============================================

export interface CreatePhrasalVerbQuizOptions {
  signals: SrsSignals<EnglishCardExt>;
  /** Returns the phrasal verb deck for distractor sourcing. */
  getPhrasalVerbs: () => Card<EnglishCardExt>[];
}

export function createPhrasalVerbQuiz(
  options: CreatePhrasalVerbQuizOptions
): QuizMode<EnglishCardExt> {
  const { signals, getPhrasalVerbs } = options;
  return {
    id: 'phrasal-verb-quiz',
    label: 'Phrasal Verbs',
    icon: '🧩',
    isAvailable: deck => deck.cards.some(c => c.tags?.includes('phrasal-verb')),
    component: ({ card }: QuizProps<EnglishCardExt>) => {
      // If routed a non-phrasal card (shouldn't happen if isAvailable filters
      // upstream), bail with a friendly message rather than crashing.
      if (!card.tags?.includes('phrasal-verb')) {
        return (
          <div className="card text-center py-12 text-slate-500">
            This card isn't a phrasal verb. Switch decks or pick another mode.
          </div>
        );
      }
      return (
        <PhrasalVerbQuizInner
          card={card}
          allCards={getPhrasalVerbs()}
          signals={signals}
        />
      );
    }
  };
}
